import { useEffect, useRef } from 'preact/hooks'

type Options = {
  shouldUpdateFn?: (props: any[]) => boolean
  disableUpdate?: boolean
}

const usePosition = <TPopup extends HTMLDivElement, TContainer extends HTMLDivElement>(
  dependProps: any[],
  options: Options = {}
) => {
  const requestRef = useRef(0)
  const prevRectRef = useRef<{
    width?: number
    left?: number
    top?: number
    height?: number
    bottom?: number
    innerHeight?: number
  }>({})
  const popupRef = useRef<TPopup>(null)
  const containerRef = useRef<TContainer>(null)
  const { shouldUpdateFn, disableUpdate } = options

  const shouldUpdate = () => {
    if (dependProps.length > 1 && typeof shouldUpdateFn === 'function') {
      return shouldUpdateFn(dependProps)
    }

    return dependProps[0]
  }

  useEffect(() => {
    const bodyOverflow = document.body.style.overflow

    const animate = () => {
      if (!disableUpdate) {
        requestRef.current = requestAnimationFrame(animate)
      }

      if (popupRef.current && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()

        if (prevRectRef.current.width !== rect.width) popupRef.current.style.width = `${rect.width}px`
        if (prevRectRef.current.bottom !== rect.bottom || prevRectRef.current.innerHeight !== rect.height) {
          popupRef.current.style.maxHeight = `${window.innerHeight - rect.bottom - 20}px`
        }
        if (prevRectRef.current.left !== rect.left) popupRef.current.style.left = `${rect.x}px`
        if (prevRectRef.current.top !== rect.top || prevRectRef.current.height !== rect.height) {
          popupRef.current.style.top = `${rect.y + rect.height}px`
        }

        prevRectRef.current = {
          width: rect.width,
          left: rect.x,
          top: rect.y,
          height: rect.height,
          bottom: rect.bottom,
          innerHeight: window.innerHeight
        }
      }
    }
    if (shouldUpdate()) {
      if (disableUpdate) {
        document.body.style.overflow = 'hidden'
      }
      requestRef.current = requestAnimationFrame(animate)
    }

    return () => {
      if (disableUpdate) {
        document.body.style.overflow = bodyOverflow
      }
      cancelAnimationFrame(requestRef.current)
      prevRectRef.current = {}
    }
  }, dependProps)

  return { popupRef, containerRef }
}

export default usePosition
